<template>
    <div class="wrapper" id="pwa-wrapper">
        <notifications></notifications>
        <div class="main-panel">
            <pwa-header
	            :isOnline="isOnline"
                :requests-count="requestsCount">
            </pwa-header>

            <div class="pwa-content" :class="{ content: !$route.meta.hideContent, 'mb-0': !isNavBarDisplayed }">
                <zoom-center-transition :duration="200" mode="out-in">
                    <router-view
	                    :isOnline="isOnline"
	                    :isLoading="isLoading"
	                    :requests-count="requestsCount"
                        @countPatchRequests="countRequestsInIndexedDB"/>
                </zoom-center-transition>
            </div>
            <div class="phone-viewport" v-if="isNavBarDisplayed">
                <md-bottom-bar md-sync-route md-type="shift">
                    <md-bottom-bar-item :to="{ name: 'Pwa Dashboard' }">
                        <font-awesome-icon class="md-icon md-bottom-bar-icon" :icon="['fa', 'home']" />
                        <span class="md-bottom-bar-label">Accueil</span>
                    </md-bottom-bar-item>
                    <md-bottom-bar-item :to="{ name: 'Pwa Planning' }">
                        <font-awesome-icon class="md-icon md-bottom-bar-icon" :icon="['fa', 'calendar']" />
                        <span class="md-bottom-bar-label">Planning</span>
                    </md-bottom-bar-item>
                </md-bottom-bar>
            </div>
        </div>
    </div>
</template>

<script>
import PwaHeader from "@/components/ProgressiveWebApp/Header";
import { mapState } from "vuex";

import indexedDBMixin from "@/mixins/indexed-db-mixin";
import applicationVersionMixin from "@/mixins/application-version-mixin";
import cacheMixin from "@/mixins/cache-mixin";

export default {
    name: "ProgressiveWebAppLayout",
    components: { PwaHeader },
	mixins: [indexedDBMixin, applicationVersionMixin, cacheMixin],
    data() {
        return {
            isOnline: true,
	        isLoading: false,
	        isRequesting: false,
	        requestsCount: 0,
	        interval: setInterval(this.updateIsOnline, 1000)
        }
    },
	computed: mapState({
		technician: state => state.pwaAuth.technician,
        isNavBarDisplayed() {
            return !(this.$route.path.includes('/report') || this.$route.path.includes('/signature') || this.$route.path.includes('/demand') || this.$route.path.includes('/closure') || this.$route.path.includes('/plans/'));
        }
	}),
    methods: {
        async getProfile() {
			if(navigator.onLine) {
				await this.$store.dispatch("pwaAuth/pwaGetTechnician", {
					access_token: JSON.parse(localStorage.getItem('pwa')).access_token
				}).then(async (response) => {
					await this.checkApplicationVersion(response.headers['x-application-version'])
				})
			} else {
				const { data } = await this.fetchDataFromIndexedDB('pwa/me')
				await this.$store.commit('pwaAuth/setTechnician', {
					pwaTechnician: data.value
				})
			}
        },
	    async sendPatchRequests() {
		    const requests = await this.getRequestsInIndexedDB('requests')
		    const failedRequests = await this.getRequestsInIndexedDB('failed_requests')
            this.requestsCount = requests.length
		    // Send requests stored in "requests" in IndexedDB
		    if(requests.length > 0) {
				// Get the oldest request stored
			    const currentRequest = requests.reduce((a, b) => (a.timestamp < b.timestamp ? a : b))
			    const currentRequestKeySplitted = currentRequest.key.split('pwa')[1]
			    const currentRequestUrl = currentRequestKeySplitted.includes('demands') ? '/demands' : currentRequestKeySplitted

			    const requestUrl = process.env.VUE_APP_API_PWA_BASE_URL + currentRequestUrl
			    this.isRequesting = true
			    try {
				    // Send request to the API
				    await fetch(requestUrl, {
					    method: 'PATCH',
					    headers: {
						    'Accept': 'application/vnd.api+json',
						    'Content-Type': 'application/vnd.api+json'
					    },
					    body: JSON.stringify(currentRequest.value)
				    }).then(async (response) => {
					    if(response.ok) {
						    // Delete request in "requests" table in IndexedDB
						    await this.deleteRequestInIndexedDB(currentRequest.key)
						    await this.checkApplicationVersion(response.headers.get('X-Application-Version'))
					    } else {
							throw new Error(`${response.statusText} (${response.status})`)
					    }
				    }).catch((e) => {
					    throw new Error(e)
				    })
			    } catch (e) {
				    // Request error, store in "failed_requests" and delete it from "requests"
				    await this.storeFailedRequest(currentRequest.key, this.technician.id, currentRequest.value, currentRequest.timestamp, e.message)
				    await this.deleteRequestInIndexedDB(currentRequest.key)

				    if(e.message === 'TypeError: Failed to fetch') {
					    this.isRequesting = false
					    this.isOnline = false
				    }
			    }
		    }

            if (requests.length === 0 && failedRequests.length > 0) {
                // Send requests stored in "failed_requests" in IndexedDB
                try {
                    await fetch(`${process.env.VUE_APP_API_PWA_BASE_URL}/failed_requests`, {
                        method: 'post',
                        headers: {
                            'Accept': 'application/vnd.api+json',
                            'Content-Type': 'application/vnd.api+json'
                        },
                        body: JSON.stringify({
                            access_token: JSON.parse(localStorage.getItem('pwa')).access_token,
                            requests: failedRequests
                        }),
                    }).then(async (response) => {
                        if(response.ok) {
                            await this.clearIndexedDBTable('failed_requests')
                            await this.countRequestsInIndexedDB()
                            await this.checkApplicationVersion(response.headers.get('X-Application-Version'))
                        }
                    }).catch((error) => {})
                } catch (e) {}
			}
		    this.isRequesting = false
        },
	    async countRequestsInIndexedDB() {
		    const requests = await this.getRequestsInIndexedDB('requests')
		    this.requestsCount = requests.length
	    },
	    async updateIsOnline() {
			// Check online status
		    this.isOnline = navigator.onLine

		    // Send patch requests automatically if online and if not requesting
            if(this.isOnline && !this.isRequesting) {
                await this.sendPatchRequests()
            }
	    },
	    async sendLogIndexedDBStatus() {
		    let statuses = await this.getTableNames()
		    let cached = await this.getCacheKey()

		    let values = ''
		    statuses.objectStoreNames.forEach((name) => {
			    values += name + '; '
		    })

		    try {
			    await fetch(`${process.env.VUE_APP_API_PWA_BASE_URL}/log_indexed_db_status`, {
				    method: 'post',
				    headers: {
					    'Accept': 'application/vnd.api+json',
					    'Content-Type': 'application/vnd.api+json'
				    },
				    body: JSON.stringify({
					    access_token: JSON.parse(localStorage.getItem('pwa')).access_token,
					    dbName: statuses.name,
					    dbTables: values,
					    dbVersion: statuses.version,
					    caches: cached.join(' ; '),
				    }),
			    }).catch((error) => {})
		    } catch (e) {}
	    }
    },
    created() {
        this.getProfile();
    },
    async mounted() {
        document.title = 'Gaia - Application Mobile'

	    await this.sendLogIndexedDBStatus()

	    // After SW update - fetch data in dynamic cache storage
	    if(JSON.parse(localStorage.getItem('pwa-cache-reload'))) {
			await this.storeInCache()
	    }
    },
	beforeDestroy() {
		clearInterval(this.interval)
	}
}
</script>

<style scoped lang="scss">
$scaleSize: 0.95;
@keyframes zoomIn95 {
    from {
        opacity: 0;
        transform: scale3d($scaleSize, $scaleSize, $scaleSize);
    }
    to {
        opacity: 1;
    }
}

.main-panel .zoomIn {
    animation-name: zoomIn95;
}
@keyframes zoomOut95 {
    from {
        opacity: 1;
    }
    to {
        opacity: 0;
        transform: scale3d($scaleSize, $scaleSize, $scaleSize);
    }
}

.main-panel .zoomOut {
    animation-name: zoomOut95;
}
</style>
